import React, { useEffect, useRef, useState } from "react";
import GoogleIcon from "../../assets/Icon.png";
import Step15Line from "../../assets/Step15Line";
import BackgroundImages from "../shared/BackgroundImages";
import LazyImage from "../shared/LazyImage";
import { buildPaywallUrl } from "../../utils/redtrack";
import {
  createPreLanderAccount,
  getGoogleAuthUrl,
  collectAndStoreUTMData,
  getExternalClickIds,
  LP_SOURCE,
} from "../../utils/api";
import TermsOfService from "./termsOfService";
import PrivacyPolicy from "./privacyPolicy";
import {
  useFormValidation,
  validationRules,
} from "../../hooks/useFormValidation";
import BackgroundVideo from "../shared/BackgroundVideo";
import { VIDEO_URLS } from "../../utils/videoConfig";
import {
  trackRegistrationModal,
  trackLanderRegistrationCompleted,
} from "../../utils/analytics";

const CreateAccount = ({ onContinue, onGoToLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleBtnRef = useRef(null);

  // Use the custom validation hook
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: "", password: "" },
    {
      email: validationRules.email,
      password: validationRules.password,
    }
  );

  // Registration form opened.
  useEffect(() => {
    trackRegistrationModal();
  }, []);

  useEffect(() => {
    // Async because externalClickIds keys come from a backend call. The Google
    // href is set once the fetch resolves; an early tap before resolve falls
    // back to the placeholder href (no harm — onClick still fires).
    let cancelled = false;
    (async () => {
      const utmObj = collectAndStoreUTMData();
      const externalClickIds = await getExternalClickIds();
      if (cancelled || !googleBtnRef.current) return;
      const state = { ...utmObj, lp_source: LP_SOURCE, externalClickIds };
      googleBtnRef.current.href = `${getGoogleAuthUrl()}?state=${encodeURIComponent(
        JSON.stringify(state)
      )}`;
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    if (!validateAll()) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const data = await createPreLanderAccount(values.email, values.password);

      if (data.redirectUrl) {
        setError("Email already used");
        return;
      }

      if (data.success) {
        trackLanderRegistrationCompleted(values.email);
        const paywallUrl = buildPaywallUrl(data.authToken, "login_paywall");
        window.location.href = paywallUrl;
      } else {
        setError(data.message || "Account creation failed");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptPrivacy = () => {
    setShowPrivacy(false);
  };

  const handleAcceptTerms = () => {
    setShowTerms(false);
  };

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps("email");
    if (emailProps.hasError) return "cardStep15Input error-input";
    return "cardStep15Input";
  };

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps("password");
    if (passwordProps.hasError) return "cardStep15Input error-input";
    return "cardStep15Input";
  };

  return (
    <>
      <div className="containerStep15">
        <BackgroundVideo className="bgVideoStep11" src={VIDEO_URLS.video3} />

        <div className="cardStep15">
          <div className="cardStep15Texts">
            <h2 className="account-title">Create Your Account</h2>
            <p className="account-subtitle">She is waiting for you inside...</p>
          </div>

          <div className="cardStep15Content">
            <div className="cardStep15Top">
              <a
                ref={googleBtnRef}
                href=" "
                className="cardStep15Goggle"
              >
                <LazyImage src={GoogleIcon} alt="Google" /> Sign up with Google
              </a>

              <div className="cardStep15Or">
                <Step15Line /> or <Step15Line className="Step15LineRight" />
              </div>

              {/* Wrap inputs and submit in a form */}
              <form
                onSubmit={handleSubmit}
                noValidate
                style={{ width: "100%" }}
              >
                <div className="cardStep15Inputs">
                  <div className="input-wrapper">
                    <input
                      placeholder="Email"
                      type="email"
                      value={getFieldProps("email").value}
                      onChange={getFieldProps("email").onChange}
                      onBlur={getFieldProps("email").onBlur}
                      className={getEmailInputClass()}
                      required
                      aria-invalid={getFieldProps("email").hasError}
                      aria-describedby={
                        getFieldProps("email").hasError
                          ? "create-email-error"
                          : undefined
                      }
                      autoComplete="email"
                    />
                    {error && (
                      <div className="error-message-input">{error}</div>
                    )}
                  </div>

                  <div className="cardStep15InputWrap">
                    <div className="input-wrapper">
                      <input
                        placeholder="Password"
                        type={showPassword ? "text" : "password"}
                        value={getFieldProps("password").value}
                        onChange={getFieldProps("password").onChange}
                        onBlur={getFieldProps("password").onBlur}
                        className={getPasswordInputClass()}
                        required
                        minLength={6}
                        aria-invalid={getFieldProps("password").hasError}
                        aria-describedby={
                          getFieldProps("password").hasError
                            ? "create-password-error"
                            : undefined
                        }
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="cardStep15InputSvg"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                              stroke="#9B9CAA"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                              stroke="#9B9CAA"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                              stroke="#9B9CAA"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                    {(getFieldProps("email").hasError ||
                      getFieldProps("password").hasError) && (
                      <div className="error-message-input">
                        Please check your email or password
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="cardStep15Btn"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </button>
              </form>

              <div className="terms-text">
                By signing up, you agree to{" "}
                <span
                  onClick={() => setShowTerms(true)}
                  type="button"
                  className="terms-link"
                >
                  Terms of Service
                </span>{" "}
                &{" "}
                <span
                  onClick={() => setShowPrivacy(true)}
                  type="button"
                  className="terms-link"
                >
                  Privacy Policy
                </span>
              </div>

              <div className="loginBtn" onClick={onGoToLogin}>
                Already have an account?{" "}
                <span>
                  Log in{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="5"
                    height="6"
                    viewBox="0 0 5 6"
                    fill="none"
                  >
                    <path
                      d="M1.375 5.25L3.625 3L1.375 0.75"
                      stroke="#E0DFF5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {showPrivacy && (
            <PrivacyPolicy
              isOpen={showPrivacy}
              onClose={() => setShowPrivacy(false)}
              onAccept={handleAcceptPrivacy}
            />
          )}
          {showTerms && (
            <TermsOfService
              isOpen={showTerms}
              onClose={() => setShowTerms(false)}
              onAccept={handleAcceptTerms}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CreateAccount;
